import { type ReactNode, useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import {
  Badge,
  cn,
  Flex,
  Icon,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Text,
} from '@kognitos/lattice';
import { useIsOverflowing, useResolvedFileDisplayName } from '@/shared/hooks';
import { formatBytes, truncateFilename, truncateString } from '@/shared/utils';
import { ValuePreviewCard } from './ValuePreviewCard';
import { PreviewLayover } from './PreviewLayover';
import {
  getOutputIconType,
  getResolvedFileDisplayName,
  isFile,
  isRemoteValue,
} from '../utils';
import { downloadOutput } from '../utils/download';
import { CHIP_MAX_LABEL_CHARS } from '../constants';
import type { TResolvedFile, TResolvedValue } from '../types';

/**
 * Props for the ValueChip component.
 */
export interface IValueChipProps {
  /** Display name for the value. Titles the popover; shown on the chip when `showName`. */
  name: string;
  /** The resolved value this chip represents. */
  value: TResolvedValue;
  /**
   * Lead the chip with `name` instead of only a value summary. Used where the
   * chip is the only place the name appears (a step's inputs); omitted where a
   * surrounding table already has a Name column.
   */
  showName?: boolean;
  /** Show a file's size beside its name. */
  showSize?: boolean;
  /**
   * Summarise an object by a name-like field (or its first scalar field)
   * instead of the bare "Object" label.
   */
  canPeek?: boolean;
  /** Extra classes for the badge (e.g. the runs-table pill fill). */
  className?: string;
}

/**
 * Shared chrome for every chip: the badge, the hover popover, and the preview
 * layover the popover can expand into.
 *
 * Keeps the interaction layer in one place so a file chip and a scalar chip
 * behave identically; only the label differs.
 *
 * No tooltip on the trigger: `OutputCard` already shows the full name (with its
 * own tooltip when it truncates) and opens on the same hover, so a tooltip here
 * would only paint over it — lattice's `Tooltip` supplies its own provider with
 * `delayDuration: 0`, so both fired on the same pointer event.
 */
function ChipShell({
  name,
  value,
  displayName,
  className,
  previewOnHover = true,
  children,
}: {
  name: string;
  value: TResolvedValue;
  /** Resolved title for the popover card, when it differs from `name`. */
  displayName?: string;
  className?: string;
  previewOnHover?: boolean;
  children: ReactNode;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [previewData, setPreviewData] = useState<{
    name: string;
    value: TResolvedValue;
  } | null>(null);

  const handleExpand = useCallback(
    (expandName: string, expandValue: TResolvedValue) => {
      setPreviewData({ name: expandName, value: expandValue });
    },
    [],
  );

  const handlePreviewClose = useCallback(() => setPreviewData(null), []);
  const handleDownload = useCallback(
    () => downloadOutput(value, name),
    [value, name],
  );

  return (
    <>
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        {/* PopoverTrigger renders its own button, so Enter and Space work
            without a keydown handler. Badge is a <span> and would not. */}
        <PopoverTrigger
          className="inline-flex max-w-full cursor-pointer"
          onMouseEnter={() => setIsOpen(previewOnHover)}
          onMouseLeave={() => setIsOpen(false)}
          // Open, never toggle, and never let the click travel further.
          //
          // preventDefault suppresses Radix's own toggle (it composes ours
          // first), which would otherwise dismiss the card hover just opened.
          // stopPropagation keeps the click off OutputCardWrapper's value area,
          // which the chip sits inside and which expands the whole output — so a
          // click meant for one file expanded the entire list into the preview
          // layover. Opening here keeps Enter and Space working, since both
          // arrive as a click.
          onClick={event => {
            event.preventDefault();
            event.stopPropagation();
            setIsOpen(true);
          }}
        >
          <Badge
            variant="outline"
            className={cn('max-w-full', className)}
            data-testid={`value-chip-${name}`}
          >
            <Flex gap={1} align="center" className="min-w-0">
              {children}
            </Flex>
          </Badge>
        </PopoverTrigger>
        <PopoverContent
          side="top"
          align="start"
          className="max-h-64 w-80 overflow-auto border-none p-0"
          onMouseEnter={() => setIsOpen(true)}
          onMouseLeave={() => setIsOpen(false)}
        >
          <ValuePreviewCard
            name={name}
            displayName={displayName}
            value={value}
            onExpand={handleExpand}
            onDownload={handleDownload}
          />
        </PopoverContent>
      </Popover>
      {previewData && (
        <PreviewLayover
          isOpen
          outputName={previewData.name}
          outputValue={previewData.value}
          onClose={handlePreviewClose}
          onDownload={downloadOutput}
        />
      )}
    </>
  );
}

/**
 * Chip for a file value.
 *
 * Resolves the real filename through Bishop metadata (so a base64 storage ID is
 * never shown) and middle-truncates it, because files in one step routinely
 * share a long prefix and head-truncation makes them identical.
 */
function FileValueChip({
  name,
  value,
  showName = false,
  showSize = true,
  className,
}: IValueChipProps & { value: TResolvedFile }) {
  const { t } = useTranslation('outputs');
  const fileUrl = value.type === 'file-remote' ? value.fileUrl : undefined;
  const fallback = getResolvedFileDisplayName(value) ?? t('outputs.chip.file');
  const { displayName, sizeBytes } = useResolvedFileDisplayName(
    fileUrl,
    fallback,
  );

  // Middle-truncated in JS: CSS can only ellipsise the end, which is the wrong
  // end for filenames that share a prefix.
  const truncated = truncateFilename(displayName, CHIP_MAX_LABEL_CHARS);
  const sizeLabel = showSize ? formatBytes(sizeBytes) : null;

  return (
    <ChipShell
      name={name}
      value={value}
      displayName={displayName}
      className={className}
    >
      <Icon type="FileType" size="xs" className="shrink-0" />
      {/* Two file inputs on one step are otherwise indistinguishable as to
          which argument each filled. */}
      {showName && (
        <Text as="span" level="xSmall" className="shrink-0">
          {name}
        </Text>
      )}
      <Text
        as="span"
        level="xSmall"
        color={showName ? 'muted' : 'default'}
        className="truncate"
      >
        {truncated}
      </Text>
      {sizeLabel && (
        <Text as="span" level="xSmall" color="muted" className="shrink-0">
          {sizeLabel}
        </Text>
      )}
    </ChipShell>
  );
}

/** Fields that usually say which record an object is, in preference order. */
const PEEK_KEYS = [
  'name',
  'title',
  'label',
  'display_name',
  'full_name',
  'id',
  'key',
];

/**
 * A short identifying summary of a plain object: its first name-like scalar
 * field, else its first scalar field as `key: value`.
 *
 * @param value - Object to summarise
 * @returns The summary, or null when the object has no scalar fields
 */
function getObjectPeek(value: object): string | null {
  const scalarEntries = Object.entries(value).filter(
    ([, field]) =>
      (typeof field === 'string' && field.trim() !== '') ||
      typeof field === 'number' ||
      typeof field === 'boolean',
  );
  for (const key of PEEK_KEYS) {
    const match = scalarEntries.find(([field]) => field.toLowerCase() === key);
    if (match) return String(match[1]);
  }
  const [first] = scalarEntries;
  return first ? `${first[0]}: ${String(first[1])}` : null;
}

/**
 * One-line summary of a non-file value: the text or number itself, an item
 * count for a list, or (with `canPeek`) a name-like field for an object.
 *
 * @param value - Value to summarise
 * @param t - Translator for the `outputs` namespace
 * @param options - `canPeek` summarises objects by a field instead of "Object"
 * @returns The summary
 */
export function getValueSummary(
  value: TResolvedValue,
  t: TFunction<'outputs'>,
  { canPeek = false }: { canPeek?: boolean } = {},
): string {
  let summary: string;
  if (value === null) summary = t('outputs.chip.null');
  else if (typeof value === 'number') summary = String(value);
  // Capped in JS, not just by CSS: `truncate` hides the overflow but
  // `white-space: nowrap` still lays out the whole string, once per chip, and the
  // runs table renders a chip per input per row. A large text output is exactly
  // the value this view has to survive.
  else if (typeof value === 'string')
    summary = truncateString(value, CHIP_MAX_LABEL_CHARS);
  else if (Array.isArray(value)) {
    summary =
      value.length === 0
        ? t('outputs.chip.emptyList')
        : t('outputs.chip.itemCount', { count: value.length });
  } else if (
    typeof value === 'object' &&
    'type' in value &&
    String((value as { type?: string }).type).startsWith('table-')
  ) {
    summary = t('outputs.chip.table');
  } else if (typeof value === 'object' && value !== null) {
    // A remote value's fields are storage plumbing, not something to preview.
    const objectPeek =
      canPeek && !isRemoteValue(value) ? getObjectPeek(value) : null;
    summary = objectPeek
      ? truncateString(objectPeek, CHIP_MAX_LABEL_CHARS)
      : t('outputs.chip.object');
  } else {
    summary = '—';
  }
  return summary;
}

/**
 * Chip for a non-file value.
 *
 * Deliberately terse — the chip is an identity marker, not a value display.
 * The popover carries the actual content.
 */
function SimpleValueChip({
  name,
  value,
  showName = false,
  canPeek = false,
  className,
}: IValueChipProps) {
  const { t } = useTranslation('outputs');
  const summary = getValueSummary(value, t, { canPeek });
  const labelRef = useRef<HTMLSpanElement>(null);
  const isOverflowing = useIsOverflowing(labelRef, 'horizontal', summary);
  const isFullyShown =
    (typeof value === 'string' || typeof value === 'number') &&
    summary === String(value) &&
    !isOverflowing;

  return (
    <ChipShell
      name={name}
      value={value}
      className={className}
      previewOnHover={!isFullyShown}
    >
      <Icon type={getOutputIconType(value)} size="xs" className="shrink-0" />
      {/* The width cap gives `truncate` something to bite on; the Badge itself
          is `w-fit` and would just grow to fit a long string. */}
      <span ref={labelRef} className="min-w-0 max-w-[32ch] truncate">
        {showName && (
          <Text as="span" level="xSmall">
            {`${name} `}
          </Text>
        )}
        <Text as="span" level="xSmall" color={showName ? 'muted' : 'default'}>
          {summary}
        </Text>
      </span>
    </ChipShell>
  );
}

/**
 * A compact, identity-first chip for a single output or input value.
 *
 * Shows what the value *is* — filename and size for files, a short summary for
 * everything else — without fetching any content. Hovering opens a popover with
 * the full value via `OutputCard`; expanding from there routes to the
 * `PreviewLayover`.
 *
 * @param props - Component props
 * @returns Chip with hover popover and preview layover
 */
export function ValueChip(props: IValueChipProps) {
  // Two components rather than one branching component: the file variant needs
  // a metadata hook the scalar variant must not run.
  return isFile(props.value) ? (
    <FileValueChip {...props} value={props.value as TResolvedFile} />
  ) : (
    <SimpleValueChip {...props} />
  );
}
